/**
 * Seeded randomness.
 *
 * Every engine takes an Rng rather than calling Math.random() internally. Two
 * reasons, both of which matter more than they look:
 *
 *  - A game is reproducible from its seed, so a failing test is a fixed seed
 *    rather than a story about what happened once.
 *  - In multiplayer the server owns the seed, so it can replay or resume a room
 *    without every client having to agree about randomness.
 */
export interface Rng {
  /** A float in [0, 1). */
  next(): number
  /** An integer in [0, maxExclusive). */
  int(maxExclusive: number): number
  /** One item, chosen uniformly. Throws on an empty list. */
  pick<T>(items: readonly T[]): T
  /** A new shuffled array; the input is not modified. */
  shuffle<T>(items: readonly T[]): T[]
}

/** Turns a string seed into a 32-bit integer. */
function hashSeed(seed: string): number {
  let h = 2166136261
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

/**
 * mulberry32 — small, fast, and good enough for shuffling word lists and
 * dealing cards. Not for anything security-related; session ids come from
 * crypto, not from here.
 */
export function createRng(seed: number | string): Rng {
  let state = (typeof seed === 'string' ? hashSeed(seed) : seed >>> 0) || 1

  const next = (): number => {
    state = (state + 0x6d2b79f5) >>> 0
    let t = state
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }

  const int = (maxExclusive: number): number => {
    if (!Number.isInteger(maxExclusive) || maxExclusive < 1) {
      throw new RangeError(`int() needs a positive integer bound, got ${maxExclusive}`)
    }
    return Math.floor(next() * maxExclusive)
  }

  return {
    next,
    int,

    pick<T>(items: readonly T[]): T {
      if (items.length === 0) throw new RangeError('pick() needs a non-empty list')
      return items[int(items.length)] as T
    },

    shuffle<T>(items: readonly T[]): T[] {
      // Fisher-Yates on a copy. Shuffling in place would mutate a caller's
      // array, and engines are meant to be pure.
      const out = [...items]
      for (let i = out.length - 1; i > 0; i--) {
        const j = int(i + 1)
        ;[out[i], out[j]] = [out[j] as T, out[i] as T]
      }
      return out
    },
  }
}
